import React, { useState } from "react";
import { UserCog, PlusCircle, RefreshCw, X } from "lucide-react";
import { useLang } from "@/i18n";
import { fmtDate } from "@/lib/time";

function metaFor(type, t) {
  if (type === "registered") return { Icon: PlusCircle, color: "#059669", bg: "#ECFDF5", bd: "#A7F3D0", label: t("registered") };
  if (type === "admin_edit") return { Icon: UserCog, color: "#B45309", bg: "#FFFBEB", bd: "#FDE68A" };
  return { Icon: RefreshCw, color: "#2563EB", bg: "#EFF6FF", bd: "#BFDBFE" };
}

export default function ActivityFeed({ events, lang }) {
  const { t } = useLang();
  const [sel, setSel] = useState(null);
  const de = lang === "de";

  if (!events || events.length === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center text-sm text-gray-500" data-testid="activity-feed">
        {t("no_activity")}
      </div>
    );
  }

  return (
    <div data-testid="activity-feed">
      <div className="flex items-center gap-2 mb-3 text-xs font-mono uppercase tracking-wider text-gray-500">
        <span className="w-2 h-2 rounded-full bg-emerald-500 pulse-dot" />
        {lang === "de" ? "Live" : "Live"} · {events.length} {lang === "de" ? "Ereignisse" : "events"}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {events.map((e, i) => {
          const m = metaFor(e.type, t);
          return (
            <div key={i} onClick={() => setSel(e)} data-testid={`activity-card-${i}`} className="flex items-start gap-3 bg-white border border-gray-200 rounded-xl p-3.5 cursor-pointer hover:border-[#c00000]/40 hover:shadow-md hover:-translate-y-0.5 transition-all">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: m.bg, border: `1px solid ${m.bd}` }}>
                <m.Icon className="w-4 h-4" style={{ color: m.color }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-gray-900 text-sm truncate">{e.device_code || e.hostname}</span>
                  <span className="text-[11px] text-gray-500 font-mono shrink-0">{fmtDate(e.timestamp)}</span>
                </div>
                <div className="text-[13px] text-gray-600 mt-1">
                  {e.type === "registered" ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ background: m.bg, color: m.color, border: `1px solid ${m.bd}` }}>{m.label}</span>
                  ) : (
                    <span className="break-words">
                      <span className="font-mono text-gray-700 font-medium">{e.field}</span>
                      {": "}
                      <span className="text-gray-500">{e.old_value || "∅"}</span>
                      {" → "}
                      <span className="text-gray-900 font-medium">{e.new_value}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="activity-detail">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSel(null)} />
          <div className="relative bg-white rounded-2xl border border-gray-200 shadow-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-lg text-gray-900">{de ? "Ereignis-Details" : "Event details"}</h3>
              <button data-testid="activity-detail-close" onClick={() => setSel(null)} className="text-gray-400 hover:text-gray-900"><X className="w-5 h-5" /></button>
            </div>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between gap-4"><dt className="text-gray-500">{de ? "Zeit" : "Time"}</dt><dd className="font-mono text-gray-900 text-right">{fmtDate(sel.timestamp)}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-gray-500">{de ? "Gerät" : "Device"}</dt><dd className="font-mono font-semibold text-gray-900 text-right">{sel.device_code || sel.hostname || "—"}</dd></div>
              <div className="flex justify-between gap-4"><dt className="text-gray-500">{de ? "Typ" : "Type"}</dt><dd className="text-gray-900 text-right">{sel.type === "registered" ? (de ? "Registriert" : "Registered") : (de ? "Geändert" : "Changed")}</dd></div>
              {sel.field && <div className="flex justify-between gap-4"><dt className="text-gray-500">{de ? "Feld" : "Field"}</dt><dd className="font-mono text-gray-900 text-right">{sel.field}</dd></div>}
              {sel.type !== "registered" && (
                <>
                  <div className="flex justify-between gap-4"><dt className="text-gray-500">{de ? "Vorher" : "Before"}</dt><dd className="font-mono text-gray-600 text-right break-all">{sel.old_value || "∅"}</dd></div>
                  <div className="flex justify-between gap-4"><dt className="text-gray-500">{de ? "Nachher" : "After"}</dt><dd className="font-mono font-semibold text-gray-900 text-right break-all">{sel.new_value || "—"}</dd></div>
                </>
              )}
              {(sel.actor || sel.user || sel.changed_by) && <div className="flex justify-between gap-4"><dt className="text-gray-500">{de ? "Durch" : "By"}</dt><dd className="text-gray-900 text-right">{sel.actor || sel.user || sel.changed_by}</dd></div>}
            </dl>
          </div>
        </div>
      )}
    </div>
  );
}
